import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  People,
  School,
  Schedule,
  CheckCircle,
  Warning,
  Refresh,
  TrendingUp,
  Assessment,
  ChevronRight,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentAttendance, setRecentAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, attendanceRes] = await Promise.all([
        api.get('/admin/dashboard/stats'),
        api.get('/admin/attendance/recent'),
      ]);
      
      setStats(statsRes.data);
      setRecentAttendance(attendanceRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const statCards = [
    {
      title: 'Total Students',
      value: stats?.totalStudents || 0,
      icon: <People sx={{ fontSize: 40, color: '#1976d2' }} />,
      color: '#e3f2fd',
    },
    {
      title: 'Total Faculty',
      value: stats?.totalFaculty || 0,
      icon: <School sx={{ fontSize: 40, color: '#2e7d32' }} />,
      color: '#e8f5e9',
    },
    {
      title: 'Active Subjects',
      value: stats?.activeSubjects || 0,
      icon: <Schedule sx={{ fontSize: 40, color: '#ed6c02' }} />,
      color: '#fff3e0',
    },
    {
      title: "Today's Attendance",
      value: `${stats?.todayAttendanceRate || 0}%`,
      icon: <CheckCircle sx={{ fontSize: 40, color: '#9c27b0' }} />,
      color: '#f3e5f5',
    },
  ];

  const attendanceData = [
    { day: 'Mon', present: 85, absent: 15 },
    { day: 'Tue', present: 90, absent: 10 },
    { day: 'Wed', present: 88, absent: 12 },
    { day: 'Thu', present: 92, absent: 8 },
    { day: 'Fri', present: 87, absent: 13 },
    { day: 'Sat', present: 45, absent: 55 },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Dashboard
        </Typography>
        <IconButton onClick={fetchDashboardData}>
          <Refresh />
        </IconButton>
      </Box>

      {/* Attendance Reports Card inspired by user image */}
      <Card 
        sx={{ 
          p: 2, 
          mb: 4, 
          display: 'flex', 
          alignItems: 'center', 
          borderRadius: 2,
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 3
          }
        }}
        onClick={() => navigate('/admin/reports')}
      >
        <Box sx={{ 
          backgroundColor: '#A855F7', 
          p: 1.5, 
          borderRadius: 3, 
          mr: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Assessment sx={{ color: 'white', fontSize: 32 }} />
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight="bold">Attendance Reports</Typography>
          <Typography variant="body2" color="text.secondary">View and export attendance reports</Typography>
        </Box>
        <ChevronRight sx={{ color: 'text.disabled' }} />
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ backgroundColor: card.color }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold">
                      {card.value}
                    </Typography>
                  </Box>
                  {card.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Attendance Chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Attendance Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={attendanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="present" name="Present" fill="#4caf50" />
                  <Bar dataKey="absent" name="Absent" fill="#f44336" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* System Status */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Chip
                  icon={<CheckCircle />}
                  label="Face Recognition Active"
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<CheckCircle />}
                  label="Database Connected"
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<CheckCircle />}
                  label="Notifications Active"
                  color="success"
                  variant="outlined"
                />
                <Chip
                  icon={<TrendingUp />}
                  label={`Accuracy: ${stats?.accuracy || 95}%`}
                  color="info"
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Attendance */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Attendance Records
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentAttendance.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{record.studentName}</TableCell>
                        <TableCell>{record.subjectName}</TableCell>
                        <TableCell>
                          {new Date(record.checkinTime).toLocaleTimeString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${(record.confidence * 100).toFixed(1)}%`}
                            color={record.confidence > 0.85 ? "success" : "warning"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={record.status === 'PRESENT' ? <CheckCircle /> : <Warning />}
                            label={record.status}
                            color={record.status === 'PRESENT' ? "success" : "error"}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;